import React, { useState } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';

const useCases = [
  {
    id: 'sales',
    label: 'Sales Teams',
    quote: '"We now reach buyers before they even talk to a competitor."',
    author: 'Felix Frank',
    role: 'Founder at StackOptimise',
    intro: 'Sales teams can build targeted lists in seconds, reach the right decision makers with verified emails and mobile numbers, and book more meetings from the same activity.',
    points: [
      { num: '01', title: 'Data Reps Can Trust', desc: 'Give reps verified emails and mobile numbers so they stop fixing lists and focus on booking meetings.' },
      { num: '02', title: 'Talk to buyers who are in market', desc: 'Use intent-style filters like buying intent, headcount growth, and tech usage to target companies actively looking for your product.' },
      { num: '03', title: 'Prospecting that feels easy', desc: 'Build targeted lists in seconds, export clean data or capture contacts with the extension, then send directly from your sequencer.' },
      { num: '04', title: 'Pay only for good data', desc: 'You only pay for verified emails and mobile numbers, at a fair price, so you can give every rep more leads without blowing up the budget.' },
    ],
  },
  {
    id: 'agencies',
    label: 'Outbound Agencies',
    quote: '"Director Discovery helped our team prioritize high-quality leads and cut through the noise."',
    author: 'Tai Shver',
    role: 'Manager of Business Development at Loopio',
    intro: 'Outbound agencies can run cold outbound and lead generation for multiple clients with stable deliverability, better reply rates, and stronger results.',
    points: [
      { num: '01', title: 'More replies for your clients', desc: 'Better data means more opened emails, more positive replies, and more meetings you can show in your reports.' },
      { num: '02', title: 'Less domain rotation, more time', desc: 'Clean, verified data keeps bounce rates low, so you spend less time buying new domains, warming inboxes, and repairing reputation.' },
      { num: '03', title: 'Reach buyers who actually want the offer', desc: 'Use intent-style filters like buying intent, headcount growth, and tech usage to target the right companies.' },
      { num: '04', title: 'Best data at the best price', desc: 'You get high-quality, fresh leads data, and pay only for verified emails and mobile numbers to protect your margins.' },
    ],
  },
  {
    id: 'founders',
    label: 'Founders',
    quote: '"95% of emails delivered, we\'ve never had results like this."',
    author: 'Gleb Kolyada',
    role: 'Founder at Datazn',
    intro: 'Founders can test new audiences, build targeted lists of thousands of contacts in seconds, and turn outbound into real pipeline with verified data.',
    points: [
      { num: '01', title: 'Test audiences in a day', desc: 'Build targeted lists of thousands of contacts in seconds, export verified emails, and start talking to them the same day.' },
      { num: '02', title: 'Reach people who actually need your product', desc: 'Use intent-style filters to target companies actively looking for your type of product, not just anyone in your market.' },
      { num: '03', title: 'More meetings without hiring', desc: 'With one tool for your whole lead gen, you can find emails, build targeted lists, and keep pipeline growing as a founder.' },
      { num: '04', title: 'Keep costs and risk low', desc: 'You only pay for validated and verified data, at a lower price, so you can keep testing campaigns without burning runway.' },
    ],
  },
  {
    id: 'revops',
    label: 'RevOps',
    quote: '"24% Increase in Meeting Booking Rate"',
    author: 'Henry Clayton',
    role: 'Principal SDR at Allego',
    intro: 'RevOps teams can standardize data, protect domains, and give every rep more of the right leads at a clear cost per lead.',
    points: [
      { num: '01', title: 'Standardize GTM inputs', desc: 'Give every team the same verified emails and mobile numbers from one source, so performance depends on playbooks and skills.' },
      { num: '02', title: 'Protect deliverability', desc: 'Verified contacts go through strong checks before they reach your sequences, so bounce rates stay low and domains stay healthy.' },
      { num: '03', title: 'Keep ICP lists focused', desc: 'Combine buying intent, headcount growth, and tech filters with regular refresh so segments stay close to your real ICP.' },
      { num: '04', title: 'Clear cost per lead', desc: 'You only pay for validated and verified contacts, cheaper than other providers, so it\'s simple to track what each lead costs.' },
    ],
  },
];

export default function UseCases() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm text-indigo-600 font-semibold uppercase tracking-wider mb-2">Use cases</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">See how Director Discovery can help you</h2>
        </div>

        <TabGroup>
          <TabList className="flex gap-2 bg-gray-100 rounded-xl p-1.5 max-w-xl mx-auto mb-12">
            {useCases.map((uc) => (
              <Tab key={uc.id} className={({ selected }) =>
                `flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all outline-none ${
                  selected ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                }`
              }>
                {uc.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {useCases.map((uc) => (
              <TabPanel key={uc.id} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left — image placeholder + quote */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl aspect-[4/3] flex items-center justify-center border border-indigo-100">
                    <div className="text-center p-6">
                      <div className="text-6xl mb-4">
                        {uc.id === 'sales' ? '📈' : uc.id === 'agencies' ? '🏢' : uc.id === 'founders' ? '🚀' : '⚙️'}
                      </div>
                      <p className="text-indigo-700 font-semibold text-lg">{uc.label}</p>
                    </div>
                  </div>
                  <blockquote className="bg-indigo-600 text-white rounded-2xl p-6">
                    <p className="text-lg font-bold mb-4">{uc.quote}</p>
                    <div>
                      <p className="font-semibold text-sm">{uc.author}</p>
                      <p className="text-indigo-300 text-xs">{uc.role}</p>
                    </div>
                  </blockquote>
                </div>

                {/* Right — points */}
                <div>
                  <p className="text-gray-600 mb-8 text-base leading-relaxed">{uc.intro}</p>
                  <div className="space-y-6">
                    {uc.points.map((pt) => (
                      <div key={pt.num} className="flex gap-4">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-indigo-600">{pt.num}</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{pt.title}</h4>
                          <p className="text-gray-500 text-sm leading-relaxed">{pt.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
      </div>
    </section>
  );
}
